import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@loader/ui';
import { Input } from '@loader/ui';
import { Label } from '@loader/ui';
import { Button } from '@loader/ui';

import { TableExplorer } from './TableExplorer';

/**
 * Props for CreateLinkedTableDialog component
 */
interface CreateLinkedTableDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when linkedTable is created */
  onCreateLinkedTable: (linkedTableData: {
    name: string;
    description: string;
    tableId: string;
    tableName: string;
  }) => void;
  /** Whether the linked table is being created */
  isCreating?: boolean;
}

/**
 * CreateLinkedTableDialog component displays a dialog for creating a new linkedTable
 * Allows users to specify the linkedTable name, description, and base table
 */
export const CreateLinkedTableDialog: React.FC<CreateLinkedTableDialogProps> = ({
  isOpen,
  onClose,
  onCreateLinkedTable,
  isCreating = false,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tableId, setTableId] = useState('');
  const [tableName, setTableName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleCreate = () => {
    if (!name.trim() || !tableId) {
      setError('Name and table are required');
      return;
    }

    onCreateLinkedTable({
      name: name.trim(),
      description: description.trim(),
      tableId,
      tableName,
    });
    // Form reset happens in handleClose when dialog closes
  };

  const handleClose = () => {
    // Reset form when closing
    setName('');
    setDescription('');
    setTableId('');
    setTableName('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Linked Table</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Linked Table Name */}
          <div className="space-y-2">
            <Label htmlFor="linkedTable-name">Linked Table Name *</Label>
            <Input
              id="linkedTable-name"
              placeholder="Enter linked table name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isCreating}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="linkedTable-description">Description</Label>
            <Input
              id="linkedTable-description"
              placeholder="Enter linked table description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isCreating}
            />
          </div>

          {/* Base Table */}
          <div className="space-y-2">
            <Label>Base Table *</Label>
            <TableExplorer
              value={tableId}
              onValueChange={(id, selectedTableName) => {
                setTableId(id);
                setTableName(selectedTableName);
              }}
              disabled={isCreating}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-red-500">{error}</div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || !tableId || isCreating}
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

